import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { createStore } from './store/store';
import { createI18nInstance } from './components/handlers/LanguageHandler';

/**
 * The heavy render path — App + Redux store + i18n + MUI + FullCalendar.
 *
 * This module is loaded on demand via `await import('./mountBooking')` from
 * `init()` in index.jsx. Keeping it behind a dynamic-import boundary is what
 * lets the entry bundle (init / initialise / TimumBooking + the loading facade)
 * stay small, so the loading spinner can paint *before* this multi-MB chunk
 * downloads — and so the chunk is fetched once (inside the iframe) instead of
 * once in the parent just to obtain `initialise`.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.rootEl     the element to render into (skeleton lives here; createRoot clears it)
 * @param {object} opts.appConfig
 * @param {object} opts.muiTheme
 * @param {object} opts.fcConfig
 * @param {string} opts.instanceId      unique id for this mount's Redux store
 * @returns {import('react-dom/client').Root}
 */
export function mountBooking({ rootEl, appConfig, muiTheme, fcConfig, instanceId }) {
  // Each mount gets its own Redux store and i18n instance so multiple widgets
  // on the same page don't share state.
  const store = createStore(instanceId);
  const i18nInstance = createI18nInstance();

  const root = createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <App
        appConfig={appConfig}
        muiTheme={muiTheme}
        fcConfig={fcConfig}
        store={store}
        i18nInstance={i18nInstance}
      />
    </React.StrictMode>,
  );

  return root;
}
